<template>
    <Teleport to="body">
        <ul v-if="slashMenuState.visible && filteredItems.length > 0" class="slash-menu" :style="menuStyle">
            <VJoyMenuItem
                v-for="(item, index) in filteredItems"
                :key="item.event"
                :class="{'slash-menu__item--active': index === selectedIndex}"
                :label="item.label"
                :icon="item.icon"
                @mousedown.prevent="selectItem(item)"
                @mouseenter="selectedIndex = index"
            />
        </ul>
    </Teleport>
</template>

<script setup lang="ts">
    import {computed, ref, watch} from 'vue';
    import {VJoyMenuItem} from '@maltjoy/core-vue';
    import type {PortfolioEditorMenuEmits} from '~/features/portfolio/editor/composable/portfolioEditorEmits';
    import {PORTFOLIO_EDITOR_ITEMS} from '~/features/portfolio/editor/composable/portfolioEditorItems';

    const props = defineProps<{
        slashMenuState: {visible: boolean; query: string; x: number; y: number; cursorTop: number};
        onDeleteSlashText: () => void;
        onHide: () => void;
    }>();

    const ITEM_HEIGHT = 36;
    const MENU_PADDING = 36;

    const menuStyle = computed(() => {
        const estimatedHeight = filteredItems.value.length * ITEM_HEIGHT + MENU_PADDING;
        const spaceBelow = window.innerHeight - props.slashMenuState.y;
        const fitsBelow = spaceBelow >= estimatedHeight;
        return {
            left: `${props.slashMenuState.x}px`,
            top: fitsBelow ? `${props.slashMenuState.y}px` : `${props.slashMenuState.cursorTop - estimatedHeight - 4}px`,
        };
    });

    const emit = defineEmits<PortfolioEditorMenuEmits>();

    const selectedIndex = ref(0);

    const filteredItems = computed(() => {
        const q = props.slashMenuState.query.toLowerCase();
        return PORTFOLIO_EDITOR_ITEMS.filter((item) => item.label.toLowerCase().startsWith(q));
    });

    watch(
        () => props.slashMenuState.query,
        () => {
            selectedIndex.value = 0;
            if (filteredItems.value.length === 0) props.onHide();
        },
    );

    watch(
        () => props.slashMenuState.visible,
        (visible) => {
            if (visible) {
                document.addEventListener('keydown', onKeyDown, true);
            } else {
                document.removeEventListener('keydown', onKeyDown, true);
                selectedIndex.value = 0;
            }
        },
    );

    function onKeyDown(event: KeyboardEvent) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            selectedIndex.value = (selectedIndex.value + 1) % filteredItems.value.length;
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            selectedIndex.value = (selectedIndex.value - 1 + filteredItems.value.length) % filteredItems.value.length;
        } else if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            const item = filteredItems.value[selectedIndex.value];
            if (item) selectItem(item);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            props.onHide();
        }
    }

    function selectItem(item: (typeof PORTFOLIO_EDITOR_ITEMS)[number]) {
        props.onDeleteSlashText();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (emit as any)(item.event);
        props.onHide();
    }
</script>

<style lang="scss" scoped>
    .slash-menu {
        position: fixed;
        z-index: 1081;
        list-style: none;
        margin: 0;
        padding: 0;
        background: var(--joy-color-neutral-0);
        border: 1px solid var(--joy-color-secondary-90);
        border-radius: var(--joy-core-radius-3);
        box-shadow: var(--joy-core-shadow-200);
        min-width: 220px;
        overflow: auto;
        max-height: 300px;

        :deep(.slash-menu__item--active) {
            background-color: var(--joy-color-secondary-10);
        }

        &::-webkit-scrollbar {
            -webkit-appearance: none;
            width: 7px;
        }
        &::-webkit-scrollbar-thumb {
            border-radius: var(--joy-core-radius-2);
            background-color: rgba(0, 0, 0, 0.3);
            -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
        }

        &__icon {
            color: var(--joy-color-text-secondary);
            flex-shrink: 0;
        }
    }
</style>
